import { queryClient } from '@/config/react-query'
import {
  createReInspection,
  getAllInspectors,
  getInspectionTemplatesByInspectionType,
  getInspectionTypes,
  getOrderNumbers,
} from '@/screens/QSF/Inspection/Inspection.service'
import { isEmptyValue, toJson } from '@/utils/common'
import {
  Modal,
  Button,
  Select,
  Form,
  DatePicker,
  Spin,
  message,
  Input,
} from 'antd'
import { debounce } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import './ModalReInspection.style.less'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'

import { USER_ROLE } from '@/constant/user'
import { useAuth } from '@/providers/auth.provider'
import { inspectorSetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import { dateFormat } from '@/utils/date'
import { DATE_FORMAT } from '@/constant/date'
import { InspectionConstant } from '../../../Inspection.constant'
import FileUploadField from '@/components/FileUploadField'
import { UPLOAD_DOCUMENTS_ENDPOINT } from '@/constant/common'
import { IFileItem, useFileUpload } from '@/hook/useFileUpload'

interface Props {
  inspection: any
  visible: boolean
  userRole: USER_ROLE.ADMIN | USER_ROLE.INSPECTOR | USER_ROLE.SUPPLIER
  onClose: () => void
  onFinish: () => void
}

const { Option } = Select
const { TextArea } = Input

function ModalReInspection(props: Props) {
  const { userRole } = props
  const { user } = useAuth()
  const [form] = Form.useForm()
  const [selectedInspectionType, setSelectedInspectionType] = useState('')
  const [orderNumber, setOrderNumber] = useState('')
  const [sumLoading, setSumLoading] = useState(true)
  const [firstInitalized, setfirstInitalized] = useState(false)

  const { uploadFiles } = useFileUpload({
    endpoint: UPLOAD_DOCUMENTS_ENDPOINT,
  })

  const initialValues = {
    order_id: props.inspection?.orders[0]?.id,
    inspection_type_id: props.inspection?.inspection_type_id,
    template_id: props.inspection?.template?.id,
    planned_date:
      props.inspection?.planned_date && moment(props.inspection.planned_date),
    assigned_inspection_id: props.inspection?.inspector?.id,
    documents: props.inspection?.medias || [],
  }

  const queryOrderNumber = useQuery(
    ['ORDER_NUMBERS', orderNumber],
    async () => {
      const response = await getOrderNumbers(orderNumber)
      return response.data?.data
    },
    { enabled: !!orderNumber }
  )

  const hasInspectorSetting = useQuery(
    ['inspector'],
    async () => await inspectorSetting()
  ).data

  const queryInspectionType = useQuery(['INSPECTION_TYPES'], async () => {
    const response = await getInspectionTypes()
    const inspectionTypes = response.data?.data.map((inspectionType) => {
      const value = toJson(inspectionType.value)
      return { ...inspectionType, value }
    })
    return inspectionTypes
  })

  const queryInspectionTemplate = useQuery(
    ['INSPECTION_TEMPLATES', selectedInspectionType],
    async () => {
      return await getInspectionTemplatesByInspectionType(
        selectedInspectionType
      )
    },
    { enabled: !!selectedInspectionType }
  )

  const queryInspector = useQuery(['INSPECTORS'], async () => {
    // TODO params pagination in next enhancement
    const data = userRole === USER_ROLE.ADMIN ? await getAllInspectors() : []
    return data
  })

  const createReInspectionMutation: any = useMutation(
    async (inspection: any) => {
      return await createReInspection(inspection)
    }
  )

  useEffect(() => {
    if (props.inspection) {
      form.setFieldsValue(initialValues)
      setSelectedInspectionType(props.inspection?.inspection_type_id)
      setOrderNumber(props.inspection?.orders[0]?.number)
    }
    return () => {
      /// cleanup
      form.resetFields()
    }
  }, [props.inspection])

  const onSearchOrderNumber = async (values: string) => {
    setOrderNumber(values)
  }

  /// select  inspection type from ant selet and clear selected template from ant select ///
  const onChangeInspectionType = async (values: string) => {
    form.setFieldsValue({ inspectionTemplate: null })
    setSelectedInspectionType(values)
  }

  const onUpsert = async (inspector: any) => {
    try {
      const documentResult = await uploadFiles(inspector.documents || [])
      const uploadedDocuments = documentResult
        .map((doc: IFileItem) => doc?.id)
        .filter((id: any) => !!id)
      const assignedTo =
        (userRole === USER_ROLE.INSPECTOR && hasInspectorSetting) ||
        [USER_ROLE.SUPPLIER].includes(userRole)
          ? user.id
          : inspector?.assigned_inspection_id
      const response = await createReInspectionMutation.mutateAsync({
        reference_inspection_id: props.inspection.id,
        planned_date: inspector?.planned_date,
        assigned_inspection_id: assignedTo,
        email_list: inspector?.email_list,
        re_inspection_comment: inspector?.re_inspection_comment,
        media_ids: uploadedDocuments,
      })
      if ([200, 201].includes(response.data.statusCode)) {
        message.success('Success')
        form.resetFields()
        queryClient.refetchQueries(['INSPECTIONS'])
        queryClient.invalidateQueries([
          InspectionConstant.QUERY_KEY.INSPECTIONS_ADVANCE_SUMMARY,
        ])
        props?.onFinish()
        props.onClose()
      }
    } catch (error: any) {
      console.log('error', error)
      message.error(error?.response?.data.message)
    }
  }

  const onCreateInspection = () => {
    form
      .validateFields()
      .then((values) => {
        const inspector = {
          ...values,
          planned_date: dateFormat(values.planned_date, DATE_FORMAT.YYYYMMDD),
        }
        onUpsert(inspector)
      })
      .catch((info) => {
        console.log('Validate Failed:', info)
      })
  }

  useEffect(() => {
    if (!firstInitalized) {
      if (!isEmptyValue(props.inspection)) {
        if (
          queryOrderNumber.isFetched &&
          queryInspectionTemplate.isFetched &&
          queryInspectionType.isFetched &&
          queryInspector.isFetched
        ) {
          setSumLoading(false)
          setfirstInitalized(true)
        }
      } else {
        setSumLoading(false)
        setfirstInitalized(true)
      }
    }
  }, [
    queryOrderNumber.isLoading,
    queryInspectionTemplate.isLoading,
    queryInspectionType.isLoading,
    queryInspector.isLoading,
  ])

  const handleFormItemInspector = () => {
    if ([USER_ROLE.INSPECTOR, USER_ROLE.SUPPLIER].includes(userRole)) {
      return (
        <Form.Item label="Inspector">
          <Select
            disabled
            placeholder="Select"
            size="large"
            value={`${user.first_name} ${user.last_name}`}
          />
        </Form.Item>
      )
    }
    return (
      <Form.Item name="assigned_inspection_id" label="Inspector">
        <Select
          allowClear
          showSearch
          placeholder="Inspector"
          size="large"
          optionFilterProp="children"
          notFoundContent={
            queryInspector.isLoading ? <Spin size="small" /> : null
          }
        >
          {queryInspector?.data?.map((inspector) => (
            <Option key={inspector.id} value={inspector.id}>
              {`${inspector?.first_name || ''} ${inspector?.last_name || ''}`}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  if (sumLoading) return <LoadingComponent />

  return (
    <Modal
      className="modal-re-inspection-container"
      open={props.visible}
      onCancel={props.onClose}
      centered
      width={680}
      maskClosable={false}
      footer={[
        <Button
          key="Cancel"
          size="large"
          className="cancel-button"
          loading={createReInspectionMutation.isLoading}
          onClick={props.onClose}
        >
          {'Cancel'}
        </Button>,
        <Button
          key="upsert"
          type="primary"
          size="large"
          loading={createReInspectionMutation.isLoading}
          onClick={onCreateInspection}
        >
          {'Create'}
        </Button>,
      ]}
    >
      <Form form={form} labelCol={{ span: 7 }}>
        <div className="modal-title">Re-inspection</div>
        <Form.Item name="order_id" label="Order Number">
          <Select
            allowClear
            showSearch
            placeholder="Order No."
            size="large"
            filterOption={false}
            disabled={!!props.inspection}
            onSearch={debounce(onSearchOrderNumber, 300)}
            notFoundContent={
              queryOrderNumber.isLoading ? <Spin size="small" /> : null
            }
          >
            {queryOrderNumber?.data?.map((orderNumber) => (
              <Option key={orderNumber.id} value={orderNumber.id}>
                {orderNumber.number}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="inspection_type_id" label="Inspection Type">
          <Select
            placeholder="Inspection Type"
            style={{ width: '100%' }}
            onChange={onChangeInspectionType}
            size="large"
            allowClear
            disabled={!!props.inspection}
          >
            {queryInspectionType.data?.map((item: any) => {
              return (
                <Option key={item.id} value={item.id}>
                  {item.value.name}
                </Option>
              )
            })}
          </Select>
        </Form.Item>
        <Form.Item name={'template_id'} label="Inspection Template">
          <Select
            placeholder="Inspection Template"
            size="large"
            allowClear
            disabled={!!props.inspection}
          >
            {queryInspectionTemplate?.data?.data?.data?.map((item: any) => {
              return (
                <Option key={item?.id} value={item?.id}>
                  {item?.name}
                </Option>
              )
            })}
          </Select>
        </Form.Item>
        <Form.Item name="planned_date" label="Planned Date">
          <DatePicker
            className="w-100"
            placeholder="Planned Date"
            size="large"
            format="DD MMM YYYY"
          />
        </Form.Item>
        {handleFormItemInspector()}

        <FileUploadField
          label="Documents"
          name="documents"
          labelCol={{ span: 7 }}
          allowLargeFile={true}
          required={false}
        />

        <p className="sub-title">Set Notification</p>
        <p className="description">
          Add the <b>reason of re-inspection</b> and the email(s) to notify
          below:
        </p>

        <Form.Item name="re_inspection_comment" label="Comment">
          <TextArea autoSize placeholder="Your comment here" size="large" />
        </Form.Item>

        <Form.Item name="email_list" label="Email">
          <Select
            mode="tags"
            size="large"
            placeholder="name@domain.com"
            style={{ width: '100%' }}
            tokenSeparators={[',']}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalReInspection
